/**
 * Evaluator of data-defined (custom) sketch-object glyphs.
 *
 * Turns an {@link ObjectDefinitionSpec} + a live {@link SketchObjectFrame} +
 * param values into the flat line-segment format every sketch-object glyph
 * produces. Pipeline per group:
 *   1. per-axis resize rule maps authored coords (default footprint) to the
 *      live footprint (stretch / fixed / pinStart / pinEnd / float);
 *   2. an optional repeat expands the group into cells (linear) or rotated
 *      copies (radial) — each copy is recorded as a glyph instance, so the
 *      DXF export emits shared blocks for them;
 *   3. shapes are tessellated in AUTHORED space and every point is mapped,
 *      so non-uniform stretches turn circles into ellipses. True arcs are
 *      recorded (DXF bulges) only where the mapping is uniform;
 *   4. the group's modifiers act on the LIVE drawing: round corners on each
 *      mapped line, mirror as extra copies across the object's center.
 *
 * Pure apart from the glyph-instance side channel — no property reads; the
 * caller resolves param values (see customSketchObjectType.ts).
 */

import {
  DEFAULT_OPENING_WALL_THICKNESS,
  evaluateExpression,
  OBJECT_DEFINITION_LIMITS,
  OPENING_WALL_VARIABLE,
  DIMENSION_OFFSET_VARIABLE,
  parseExpression,
  type ExpressionNode,
  type NumberBinding,
  type ObjectAxisResize,
  type ObjectDefinitionSpec,
  type ObjectGroup,
  type ObjectLinearRepeat,
  type ObjectParamValue,
  type ObjectPathShape,
  type ObjectShape,
  type ObjectShapeLayer,
  type ObjectRepeatSkip,
  type ObjectUniformScale,
  type Vec2,
} from 'shared-types';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { at } from '../glyphs/frame';
import { beginGlyphInstance, endGlyphInstance, recordGlyphArc } from '../glyphs/glyphInstances';
import type { SketchObjectFrame } from '../types';

export type CustomParamValues = Readonly<Record<string, ObjectParamValue>>;

/** Chord count of a full circle; arcs use a proportional share (min 4). */
const CIRCLE_SEGMENTS = 48;
const DASH_LENGTH = 0.12;
const DASH_GAP = 0.08;
/** Fewest dashes around a closed dashed shape / along an open one (see SegmentSink.pushDashed). */
const MIN_CLOSED_DASHES = 8;
const MIN_OPEN_DASHES = 3;
const EPS = 1e-9;

/** Authored → live mapping of a point, plus its per-axis scale (for arc records). */
interface PointMapper {
  map(p: Vec2): Vec2;
  /** Absolute scale along u and n; equal → similarity (arcs stay arcs). */
  scaleU: number;
  scaleN: number;
}

/** Tessellated authored polyline, with its true-arc identity when it has one. */
export interface AuthoredPolyline {
  points: Vec2[];
  arc?: { center: Vec2; radius: number; sweep: number };
}

// ─── Param resolution ───────────────────────────────────────────────────

/** Fill defaults for missing / wrongly typed values. */
export function resolveCustomParams(spec: ObjectDefinitionSpec, raw: CustomParamValues): CustomParamValues {
  const out: Record<string, ObjectParamValue> = {};
  for (const p of spec.params) {
    const v = raw[p.key];
    switch (p.kind) {
      case 'float':
      case 'int':
        out[p.key] = typeof v === 'number' && Number.isFinite(v) ? v : p.default;
        break;
      case 'boolean':
        out[p.key] = typeof v === 'boolean' ? v : p.default;
        break;
      case 'select':
        out[p.key] = typeof v === 'string' && p.options.some((o) => o.value === v) ? v : p.default;
        break;
    }
  }
  return out;
}

export function defaultCustomParams(spec: ObjectDefinitionSpec): CustomParamValues {
  return resolveCustomParams(spec, {});
}

function bindingValue(binding: NumberBinding | undefined, params: CustomParamValues, vars?: Readonly<Record<string, number>>): number | undefined {
  if (!binding) return undefined;
  if ('value' in binding) return binding.value;
  if ('expr' in binding) return expressionValue(binding.expr, vars ?? {});
  const v = params[binding.param];
  return typeof v === 'number' ? v : undefined;
}

// ─── Expressions ────────────────────────────────────────────────────────

/** Parsed expressions by source text (null = invalid; the validator reports it). */
const expressionCache = new Map<string, ExpressionNode | null>();

export function expressionValue(src: string | undefined, vars: Readonly<Record<string, number>>): number {
  if (!src) return 0;
  let node = expressionCache.get(src);
  if (node === undefined) {
    const parsed = parseExpression(src);
    node = parsed.ok ? parsed.node : null;
    expressionCache.set(src, node);
  }
  return node ? evaluateExpression(node, vars) : 0;
}

/**
 * Everything expressions can read: the live size, the params (true = 1,
 * a choice = its option index) and the formulas, evaluated in order.
 * `params` must be resolved (see {@link resolveCustomParams}).
 */
export function evaluationVars(
  spec: ObjectDefinitionSpec,
  width: number,
  depth: number,
  params: CustomParamValues,
  height: number = spec.defaultSize.height,
  wall: number = DEFAULT_OPENING_WALL_THICKNESS,
  offset = 0,
): Record<string, number> {
  const vars: Record<string, number> = { width, depth, height };
  // Doors / windows also read the wall thickness at the opening, dimensions their line's offset.
  if (spec.opening) vars[OPENING_WALL_VARIABLE] = wall;
  if (spec.dimension) vars[DIMENSION_OFFSET_VARIABLE] = offset;
  for (const p of spec.params) {
    const value = params[p.key];
    if (typeof value === 'number') vars[p.key] = value;
    else if (typeof value === 'boolean') vars[p.key] = value ? 1 : 0;
    else if (p.kind === 'select') vars[p.key] = Math.max(0, p.options.findIndex((o) => o.value === value));
  }
  for (const f of spec.formulas ?? []) vars[f.key] = expressionValue(f.expr, vars);
  return vars;
}

// ─── Formula coordinates ────────────────────────────────────────────────

/**
 * The shape with its formula fields evaluated (see ObjectShapeBase.expr).
 * Shapes without formulas come back as they are.
 */
export function resolveShapeExpressions(shape: ObjectShape, vars: Readonly<Record<string, number>>): ObjectShape {
  const expr = shape.expr;
  if (!expr) return shape;
  const val = (field: string, fallback: number): number => (expr[field] !== undefined ? expressionValue(expr[field], vars) : fallback);
  const vec = (prefix: string, p: Vec2): Vec2 => [val(`${prefix}.u`, p[0]), val(`${prefix}.n`, p[1])];
  switch (shape.kind) {
    case 'polyline':
      return { ...shape, points: shape.points.map((p, i) => vec(`p${i}`, p)) };
    case 'rect': {
      const a = vec('min', shape.min);
      const b = vec('max', shape.max);
      return { ...shape, min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])], max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])] };
    }
    case 'circle':
      return { ...shape, center: vec('center', shape.center), radius: Math.max(0, val('radius', shape.radius)) };
    case 'arc':
      return { ...shape, center: vec('center', shape.center), radius: Math.max(0, val('radius', shape.radius)), startAngle: val('startAngle', shape.startAngle), endAngle: val('endAngle', shape.endAngle) };
    case 'path':
      return shape;
  }
}

/** Vars at the default size and default params (what the editor shows as the authored drawing). */
export function defaultEvaluationVars(spec: ObjectDefinitionSpec): Record<string, number> {
  return { ...evaluationVars(spec, spec.defaultSize.width, spec.defaultSize.depth, defaultCustomParams(spec)), i: 0, count: 1 };
}

/** The spec's shapes as drawn at the default size and params (formulas resolved; index copies at i = 0). */
export function resolveAuthoredShapes(spec: ObjectDefinitionSpec): ObjectShape[] {
  const vars = defaultEvaluationVars(spec);
  return spec.shapes.map((s) => resolveShapeExpressions(s, vars));
}

// ─── Axis resize ────────────────────────────────────────────────────────

/** Authored extent of a set of shapes along u (axis 0) or n (axis 1). */
export function shapesBounds(shapes: readonly ObjectShape[], axis: 0 | 1): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  const add = (v: number): void => {
    if (v < min) min = v;
    if (v > max) max = v;
  };
  for (const s of shapes) {
    switch (s.kind) {
      case 'polyline':
        for (const p of s.points) add(p[axis]);
        break;
      case 'rect':
        add(s.min[axis]);
        add(s.max[axis]);
        break;
      case 'circle':
      case 'arc':
        add(s.center[axis] - s.radius);
        add(s.center[axis] + s.radius);
        break;
      case 'path':
        // The curve itself, not the control points (they may lie well outside it).
        for (const p of pathPoints(s)) add(p[axis]);
        break;
    }
  }
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
}

/** One-axis map for `rule`: authored extent E0 → live extent E. */
function axisMap(rule: ObjectAxisResize, e0: number, e: number, bounds: { min: number; max: number }): { map: (x: number) => number; scale: number } {
  switch (rule) {
    case 'stretch': {
      const s = e0 > EPS ? e / e0 : 1;
      return { map: (x) => x * s, scale: s };
    }
    case 'fixed':
      return { map: (x) => x, scale: 1 };
    case 'pinStart': {
      const d = (e0 - e) / 2;
      return { map: (x) => x + d, scale: 1 };
    }
    case 'pinEnd': {
      const d = (e - e0) / 2;
      return { map: (x) => x + d, scale: 1 };
    }
    case 'float': {
      const center = (bounds.min + bounds.max) / 2;
      const d = e0 > EPS ? center * (e / e0 - 1) : 0;
      return { map: (x) => x + d, scale: 1 };
    }
  }
}

/** The single scale of a proportional group (see ObjectUniformScale). */
export function uniformScale(spec: ObjectDefinitionSpec, rule: ObjectUniformScale, width: number, depth: number): number {
  const su = spec.defaultSize.width > EPS ? width / spec.defaultSize.width : 1;
  const sn = spec.defaultSize.depth > EPS ? depth / spec.defaultSize.depth : 1;
  switch (rule) {
    case 'width':
      return su;
    case 'depth':
      return sn;
    case 'fit':
      return Math.min(su, sn);
    case 'fill':
      return Math.max(su, sn);
  }
}

/** A group's live map along `axis`: its resize rule, or the shared factor of a proportional group. */
function groupAxisMap(spec: ObjectDefinitionSpec, group: ObjectGroup, axis: 'u' | 'n', width: number, depth: number, bounds: { min: number; max: number }): { map: (x: number) => number; scale: number } {
  if (group.resize.uniform) {
    const s = uniformScale(spec, group.resize.uniform, width, depth);
    return { map: (x) => x * s, scale: s };
  }
  return axis === 'u' ? axisMap(group.resize.u, spec.defaultSize.width, width, bounds) : axisMap(group.resize.n, spec.defaultSize.depth, depth, bounds);
}

// ─── Tessellation (authored space) ──────────────────────────────────────

function arcPoints(center: Vec2, radius: number, a0: number, a1: number): Vec2[] {
  // The epsilon keeps exact fractions exact (a quarter turn = 12, not 13 from 12.000000000000002).
  const segments = Math.max(4, Math.ceil((Math.abs(a1 - a0) / (2 * Math.PI)) * CIRCLE_SEGMENTS - 1e-9));
  const pts: Vec2[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = a0 + ((a1 - a0) * i) / segments;
    pts.push([center[0] + radius * Math.cos(t), center[1] + radius * Math.sin(t)]);
  }
  return pts;
}

/** Samples per cubic segment (a straight segment — no handles on either side — is just its end). */
const BEZIER_SEGMENTS = 24;

/** Points on the path's curve, first node to last (back to the first when closed). */
export function pathPoints(shape: ObjectPathShape): Vec2[] {
  const { nodes } = shape;
  if (nodes.length === 0) return [];
  const pts: Vec2[] = [nodes[0].point];
  const count = shape.closed && nodes.length > 2 ? nodes.length : nodes.length - 1;
  for (let i = 0; i < count; i++) {
    const a = nodes[i];
    const b = nodes[(i + 1) % nodes.length];
    if (!a.out && !b.in) {
      pts.push(b.point);
      continue;
    }
    const p0 = a.point;
    const p1 = a.out ?? a.point;
    const p2 = b.in ?? b.point;
    const p3 = b.point;
    for (let k = 1; k <= BEZIER_SEGMENTS; k++) {
      const t = k / BEZIER_SEGMENTS;
      const m = 1 - t;
      const w0 = m * m * m;
      const w1 = 3 * m * m * t;
      const w2 = 3 * m * t * t;
      const w3 = t * t * t;
      pts.push([w0 * p0[0] + w1 * p1[0] + w2 * p2[0] + w3 * p3[0], w0 * p0[1] + w1 * p1[1] + w2 * p2[1] + w3 * p3[1]]);
    }
  }
  return pts;
}

/** A shape as polylines in authored space (the editor draws and hit-tests these). */
export function tessellateObjectShape(shape: ObjectShape): AuthoredPolyline[] {
  switch (shape.kind) {
    case 'polyline':
      return [{ points: shape.closed && shape.points.length > 2 ? [...shape.points, shape.points[0]] : [...shape.points] }];
    case 'rect': {
      const [u0, n0] = shape.min;
      const [u1, n1] = shape.max;
      return [{ points: [[u0, n0], [u1, n0], [u1, n1], [u0, n1], [u0, n0]] }];
    }
    case 'circle':
      if (shape.radius <= EPS) return [];
      return [{ points: arcPoints(shape.center, shape.radius, 0, 2 * Math.PI), arc: { center: shape.center, radius: shape.radius, sweep: 2 * Math.PI } }];
    case 'arc':
      if (shape.radius <= EPS) return [];
      return [
        {
          points: arcPoints(shape.center, shape.radius, shape.startAngle, shape.endAngle),
          arc: { center: shape.center, radius: shape.radius, sweep: shape.endAngle - shape.startAngle },
        },
      ];
    case 'path':
      return [{ points: pathPoints(shape) }];
  }
}

// ─── Modifiers (live drawing) ───────────────────────────────────────────

/** A group's active modifiers, resolved for one evaluation. */
export interface ResolvedModifiers {
  /** Corner radius (m); 0 = none. */
  round: number;
  /** Extra mirrored copies as per-axis signs ([-1, 1] = left ↔ right). */
  mirrors: ReadonlyArray<readonly [number, number]>;
}

const NO_MODIFIERS: ResolvedModifiers = { round: 0, mirrors: [] };

export function resolveModifiers(group: ObjectGroup, params: CustomParamValues, vars: Readonly<Record<string, number>>): ResolvedModifiers {
  const active = (group.modifiers ?? []).filter((m) => m.enabled !== false);
  if (active.length === 0) return NO_MODIFIERS;
  let round = 0;
  let mirrors: Array<readonly [number, number]> = [];
  for (const m of active) {
    if (m.kind === 'roundCorners') round = Math.max(0, bindingValue(m.radius, params, vars) ?? 0);
    else {
      // Mirrors compose: each one also mirrors the copies before it.
      const axes: Array<readonly [number, number]> = m.axis === 'u' ? [[-1, 1]] : m.axis === 'n' ? [[1, -1]] : [[-1, 1], [1, -1], [-1, -1]];
      const all = [[1, 1] as const, ...mirrors];
      const next = new Map<string, readonly [number, number]>(mirrors.map((x) => [`${x[0]},${x[1]}`, x]));
      for (const [su, sn] of all) for (const [au, an] of axes) next.set(`${su * au},${sn * an}`, [su * au, sn * an]);
      next.delete('1,1');
      mirrors = [...next.values()];
    }
  }
  return { round, mirrors };
}

/** Corners turning less than this are left alone (the samples of a curve). */
const MIN_CORNER_TURN = (15 * Math.PI) / 180;
/** Chord angle of a rounded corner's arc. */
const FILLET_STEP = Math.PI / 16;

/**
 * Round every corner of a (live) polyline with `radius`: each corner is
 * replaced by a circular arc tangent to both sides. A radius too big for a
 * corner shrinks so the arc ends at most halfway along each side (sharing a
 * side with the next corner). Closed polylines round their closing corner too.
 */
export function roundPolylineCorners(points: readonly Vec2[], radius: number): Vec2[] {
  if (radius <= EPS || points.length < 3) return [...points];
  const first = points[0];
  const last = points[points.length - 1];
  const closed = points.length > 3 && Math.hypot(first[0] - last[0], first[1] - last[1]) < 1e-9;
  const ring = closed ? points.slice(0, -1) : points;
  const n = ring.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const p = ring[i];
    if (!closed && (i === 0 || i === n - 1)) {
      out.push(p);
      continue;
    }
    const a = ring[(i - 1 + n) % n];
    const b = ring[(i + 1) % n];
    const la = Math.hypot(a[0] - p[0], a[1] - p[1]);
    const lb = Math.hypot(b[0] - p[0], b[1] - p[1]);
    if (la < EPS || lb < EPS) {
      out.push(p);
      continue;
    }
    const d1: Vec2 = [(a[0] - p[0]) / la, (a[1] - p[1]) / la];
    const d2: Vec2 = [(b[0] - p[0]) / lb, (b[1] - p[1]) / lb];
    const theta = Math.acos(Math.max(-1, Math.min(1, d1[0] * d2[0] + d1[1] * d2[1])));
    if (Math.PI - theta < MIN_CORNER_TURN || theta < 1e-6) {
      out.push(p);
      continue;
    }
    const tanHalf = Math.tan(theta / 2);
    const t = Math.min(radius / tanHalf, la / 2, lb / 2);
    const r = t * tanHalf;
    const bisLen = Math.hypot(d1[0] + d2[0], d1[1] + d2[1]);
    const bis: Vec2 = [(d1[0] + d2[0]) / bisLen, (d1[1] + d2[1]) / bisLen];
    const dist = r / Math.sin(theta / 2);
    const c: Vec2 = [p[0] + bis[0] * dist, p[1] + bis[1] * dist];
    const t1: Vec2 = [p[0] + d1[0] * t, p[1] + d1[1] * t];
    const t2: Vec2 = [p[0] + d2[0] * t, p[1] + d2[1] * t];
    const a0 = Math.atan2(t1[1] - c[1], t1[0] - c[0]);
    let sweep = Math.atan2(t2[1] - c[1], t2[0] - c[0]) - a0;
    while (sweep > Math.PI) sweep -= 2 * Math.PI;
    while (sweep < -Math.PI) sweep += 2 * Math.PI;
    const steps = Math.max(2, Math.ceil(Math.abs(sweep) / FILLET_STEP));
    for (let k = 0; k <= steps; k++) {
      const ang = a0 + (sweep * k) / steps;
      out.push([c[0] + r * Math.cos(ang), c[1] + r * Math.sin(ang)]);
    }
  }
  if (closed) out.push(out[0]);
  return out;
}

// ─── Emission ───────────────────────────────────────────────────────────

class SegmentSink {
  private readonly cap = OBJECT_DEFINITION_LIMITS.maxEvaluatedSegments * 6;

  constructor(
    readonly out: number[],
    private readonly frame: SketchObjectFrame,
    /** When given, dashed lines go here UNdashed (the caller dashes them on screen). */
    private readonly dashedOut: number[] | null = null,
  ) {}

  get full(): boolean {
    return this.out.length >= this.cap;
  }

  private world(p: Vec2): Pt2 {
    return at(this.frame, p[0], p[1]);
  }

  emit(poly: AuthoredPolyline, mapper: PointMapper, dashed: boolean, modifiers: ResolvedModifiers = NO_MODIFIERS): void {
    if (this.full || poly.points.length < 2) return;
    let live = poly.points.map((p) => mapper.map(p));
    // Circles and arcs have no corners to round.
    if (modifiers.round > 0 && !poly.arc) live = roundPolylineCorners(live, modifiers.round);
    const copies: ReadonlyArray<readonly [number, number]> = [[1, 1], ...modifiers.mirrors];
    for (const [su, sn] of copies) {
      if (this.full) return;
      const pts = live.map(([u, n]) => this.world([u * su, n * sn]));
      const segStart = this.out.length;
      if (dashed && this.dashedOut) for (let i = 0; i + 1 < pts.length; i++) this.dashedOut.push(pts[i].x, pts[i].y, this.frame.z, pts[i + 1].x, pts[i + 1].y, this.frame.z);
      else if (dashed) this.pushDashed(pts);
      else for (let i = 0; i + 1 < pts.length && !this.full; i++) this.push(pts[i], pts[i + 1]);
      if (poly.arc && !dashed && Math.abs(mapper.scaleU - mapper.scaleN) < 1e-9) {
        const [cu, cn] = mapper.map(poly.arc.center);
        // A mirrored copy runs the other way round.
        recordGlyphArc(this.out, this.world([cu * su, cn * sn]), poly.arc.radius * mapper.scaleU, poly.arc.sweep * su * sn, segStart);
      }
    }
  }

  private push(a: Pt2, b: Pt2): void {
    this.out.push(a.x, a.y, this.frame.z, b.x, b.y, this.frame.z);
  }

  /**
   * Dash pattern continuous along the whole polyline (tessellated curves stay
   * dashed), fitted to its length: about DASH_LENGTH + DASH_GAP per dash, but
   * at least MIN_CLOSED_DASHES around a closed shape (evenly, so a small
   * circle still reads as a dashed circle) and MIN_OPEN_DASHES along an open
   * line, which starts and ends on a dash.
   */
  private pushDashed(pts: Pt2[]): void {
    let total = 0;
    for (let i = 0; i + 1 < pts.length; i++) total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (total <= EPS) return;
    const first = pts[0];
    const last = pts[pts.length - 1];
    const closed = pts.length > 2 && Math.hypot(first.x - last.x, first.y - last.y) < 1e-9;
    const ratio = DASH_LENGTH / (DASH_LENGTH + DASH_GAP);
    const count = Math.max(closed ? MIN_CLOSED_DASHES : MIN_OPEN_DASHES, Math.round(total / (DASH_LENGTH + DASH_GAP)));
    // Closed: `count` whole periods; open: `count` dashes with `count − 1` gaps between them.
    const period = closed ? total / count : total / (count - (1 - ratio));
    const dash = period * ratio;
    const gap = period - dash;
    let drawing = true;
    let remaining = dash;
    for (let i = 0; i + 1 < pts.length && !this.full; i++) {
      let a = pts[i];
      const b = pts[i + 1];
      let len = Math.hypot(b.x - a.x, b.y - a.y);
      while (len > EPS && !this.full) {
        const step = Math.min(remaining, len);
        const t = step / len;
        const next: Pt2 = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        if (drawing) this.push(a, next);
        a = next;
        len -= step;
        remaining -= step;
        if (remaining <= EPS) {
          drawing = !drawing;
          remaining = drawing ? dash : gap;
        }
      }
    }
  }
}

function isSkipped(i: number, last: number, skip: ObjectRepeatSkip | undefined): boolean {
  if (!skip || skip === 'none') return false;
  return ((skip === 'first' || skip === 'both') && i === 0) || ((skip === 'last' || skip === 'both') && i === last);
}

/** Live [start, end] run of a linear repeat along its axis (expressions win over run + resize rule; the group offset applies). */
function linearRun(spec: ObjectDefinitionSpec, group: ObjectGroup, repeat: ObjectLinearRepeat, width: number, depth: number, bounds: { min: number; max: number }, vars: Readonly<Record<string, number>>): [number, number] {
  const axisIsU = repeat.axis === 'u';
  const shift = expressionValue(axisIsU ? group.offset?.u : group.offset?.n, vars);
  if (repeat.runExpr) return [expressionValue(repeat.runExpr.start, vars) + shift, expressionValue(repeat.runExpr.end, vars) + shift];
  const e0 = axisIsU ? spec.defaultSize.width : spec.defaultSize.depth;
  const run = repeat.run ?? [-e0 / 2, e0 / 2];
  const { map } = groupAxisMap(spec, group, repeat.axis, width, depth, bounds);
  return [map(run[0]) + shift, map(run[1]) + shift];
}

/** Cell count of a linear repeat for a run length (pitch wins over count when both resolve). */
export function linearRepeatCount(repeat: ObjectLinearRepeat, runLength: number, params: CustomParamValues, vars?: Readonly<Record<string, number>>): number {
  const pitch = bindingValue(repeat.pitch, params, vars);
  const count = bindingValue(repeat.count, params, vars);
  let n: number;
  if (pitch !== undefined && pitch > EPS) n = repeat.fit === 'exact' ? Math.floor(runLength / pitch + 1e-6) : Math.round(runLength / pitch);
  else n = Math.round(count ?? 1);
  const min = Math.max(1, repeat.minCount ?? 1);
  const max = Math.min(OBJECT_DEFINITION_LIMITS.maxRepeatCount, repeat.maxCount ?? OBJECT_DEFINITION_LIMITS.maxRepeatCount);
  return Math.min(max, Math.max(min, n));
}

/** Live run length of a group's linear repeat — the quantity pitch ↔ count sync divides. */
export function linearRepeatRunLength(spec: ObjectDefinitionSpec, group: ObjectGroup, width: number, depth: number, params: CustomParamValues = {}): number {
  if (group.repeat?.kind !== 'linear') return 0;
  const bounds = shapesBounds(authoredGroupShapes(spec, group), group.repeat.axis === 'u' ? 0 : 1);
  const [r0, r1] = linearRun(spec, group, group.repeat, width, depth, bounds, evaluationVars(spec, width, depth, resolveCustomParams(spec, params)));
  return Math.max(0, r1 - r0);
}

function groupShapes(spec: ObjectDefinitionSpec, group: ObjectGroup): ObjectShape[] {
  const ids = new Set(group.shapeIds);
  return spec.shapes.filter((s) => ids.has(s.id));
}

/** A group's shapes as authored (formulas at the default size): the extent resize rules measure against. */
function authoredGroupShapes(spec: ObjectDefinitionSpec, group: ObjectGroup): ObjectShape[] {
  const shapes = groupShapes(spec, group);
  if (!shapes.some((s) => s.expr)) return shapes;
  const vars = defaultEvaluationVars(spec);
  return shapes.map((s) => resolveShapeExpressions(s, vars));
}

function isGroupVisible(group: ObjectGroup, params: CustomParamValues): boolean {
  if (!group.visibleWhen) return true;
  const v = params[group.visibleWhen.param];
  const expected = group.visibleWhen.equals;
  return typeof v === 'number' && typeof expected === 'number' ? Math.abs(v - expected) < 1e-9 : v === expected;
}

/** Turn a mapper's output by `angle` about (cu, cn) — the group offset's rotation (a similarity: arcs stay arcs). */
function rotated(mapper: PointMapper, angle: number, cu: number, cn: number): PointMapper {
  if (!angle) return mapper;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    map: (p) => {
      const [u, n] = mapper.map(p);
      const du = u - cu;
      const dn = n - cn;
      return [cu + du * cos - dn * sin, cn + du * sin + dn * cos];
    },
    scaleU: mapper.scaleU,
    scaleN: mapper.scaleN,
  };
}

function emitGroup(sink: SegmentSink, spec: ObjectDefinitionSpec, group: ObjectGroup, frame: SketchObjectFrame, params: CustomParamValues, layer: ObjectShapeLayer, baseVars: Readonly<Record<string, number>>): void {
  const shapes = groupShapes(spec, group).filter((s) => !s.hidden && (s.layer ?? 'glyph') === layer);
  if (shapes.length === 0 || !isGroupVisible(group, params)) return;
  const allShapes = authoredGroupShapes(spec, group);
  const modifiers = resolveModifiers(group, params, baseVars);
  // Mirrored copies land outside a repeat's cells: no shared DXF blocks then.
  const instanced = modifiers.mirrors.length === 0;
  const boundsU = shapesBounds(allShapes, 0);
  const boundsN = shapesBounds(allShapes, 1);
  const baseU = groupAxisMap(spec, group, 'u', frame.width, frame.depth, boundsU);
  const baseN = groupAxisMap(spec, group, 'n', frame.width, frame.depth, boundsN);

  /** One placement of the group: shapes with formulas resolved for `vars`, offset (shift + turn) applied. */
  const place = (vars: Readonly<Record<string, number>>) => {
    if (group.visibleIf !== undefined && expressionValue(group.visibleIf, vars) === 0) return null;
    const offU = expressionValue(group.offset?.u, vars);
    const offN = expressionValue(group.offset?.n, vars);
    const turn = expressionValue(group.offset?.rotate, vars);
    const polys = shapes.map((s) => ({ polys: tessellateObjectShape(resolveShapeExpressions(s, vars)), dashed: s.style === 'dashed' }));
    const mapU = offU ? { map: (x: number) => baseU.map(x) + offU, scale: baseU.scale } : baseU;
    const mapN = offN ? { map: (x: number) => baseN.map(x) + offN, scale: baseN.scale } : baseN;
    const emitAll = (mapper: PointMapper): void => {
      const turned = rotated(mapper, turn, offU, offN);
      for (const { polys: list, dashed } of polys) for (const poly of list) sink.emit(poly, turned, dashed, modifiers);
    };
    return { mapU, mapN, emitAll };
  };

  const repeat = group.repeat;
  if (repeat?.kind === 'index') {
    // Copies placed by the formulas themselves (i = 0…count-1); no DXF blocks — copies may differ.
    const count = Math.min(OBJECT_DEFINITION_LIMITS.maxRepeatCount, Math.max(0, Math.round(bindingValue(repeat.count, params, baseVars) ?? 0)));
    for (let i = 0; i < count && !sink.full; i++) {
      const placed = place({ ...baseVars, i, count });
      placed?.emitAll({ map: (p) => [placed.mapU.map(p[0]), placed.mapN.map(p[1])], scaleU: Math.abs(placed.mapU.scale), scaleN: Math.abs(placed.mapN.scale) });
    }
    return;
  }

  const placed = place({ ...baseVars, i: 0, count: 1 });
  if (!placed) return;
  const { mapU, mapN, emitAll } = placed;
  const vars = baseVars;
  if (!repeat) {
    emitAll({ map: (p) => [mapU.map(p[0]), mapN.map(p[1])], scaleU: Math.abs(mapU.scale), scaleN: Math.abs(mapN.scale) });
    return;
  }

  if (repeat.kind === 'linear') {
    const axisIsU = repeat.axis === 'u';
    const [r0, r1] = linearRun(spec, group, repeat, frame.width, frame.depth, axisIsU ? boundsU : boundsN, vars);
    const runLength = r1 - r0;
    if (runLength <= EPS) return;
    const count = linearRepeatCount(repeat, runLength, params, vars);
    const pitch = bindingValue(repeat.pitch, params, vars);
    const exact = repeat.fit === 'exact' && pitch !== undefined && pitch > EPS;
    const cellSize = exact ? pitch : runLength / count;
    const offset = exact ? (runLength - count * cellSize) / 2 : 0;
    const [a, b] = repeat.cell;
    const authoredCell = b - a;
    const cross = axisIsU ? mapN : mapU;
    const alongScale = repeat.cellResize === 'stretch' ? cellSize / authoredCell : 1;

    for (let i = 0; i < count && !sink.full; i++) {
      if (isSkipped(i, count - 1, repeat.skip)) continue;
      const t0 = r0 + offset + i * cellSize;
      const mirrored = repeat.mirrorAlternate === true && i % 2 === 1;
      const along = (x: number): number => {
        const xm = mirrored ? a + b - x : x;
        return repeat.cellResize === 'stretch' ? t0 + (xm - a) * alongScale : t0 + cellSize / 2 + (xm - (a + b) / 2);
      };
      const center = t0 + cellSize / 2;
      const instanceCenter = axisIsU ? at(frame, center, 0) : at(frame, 0, center);
      if (instanced)
        beginGlyphInstance(sink.out, `${group.id}_${mirrored ? 'm' : 'n'}_${Math.round(cellSize * 1000)}`, {
          center: instanceCenter,
          dir: frame.dir,
          norm: frame.norm,
          width: axisIsU ? cellSize : frame.width,
          depth: axisIsU ? frame.depth : cellSize,
          z: frame.z,
        });
      emitAll({
        map: (p) => (axisIsU ? [along(p[0]), cross.map(p[1])] : [cross.map(p[0]), along(p[1])]),
        scaleU: Math.abs(axisIsU ? alongScale : cross.scale),
        scaleN: Math.abs(axisIsU ? cross.scale : alongScale),
      });
      if (instanced) endGlyphInstance(sink.out);
    }
    return;
  }

  // Radial: map into the live footprint first, then rotate about the mapped center.
  const count = Math.min(OBJECT_DEFINITION_LIMITS.maxRepeatCount, Math.max(1, Math.round(bindingValue(repeat.count, params, vars) ?? 1)));
  const c: Vec2 = [mapU.map(repeat.center[0]), mapN.map(repeat.center[1])];
  for (let i = 0; i <= count && !sink.full; i++) {
    if (isSkipped(i, count, repeat.skip)) continue;
    const theta = repeat.startAngle + (i * repeat.sweep) / count;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    if (instanced)
      beginGlyphInstance(sink.out, `${group.id}_r`, {
        center: at(frame, c[0], c[1]),
        dir: { x: cos * frame.dir.x + sin * frame.norm.x, y: cos * frame.dir.y + sin * frame.norm.y },
        norm: { x: -sin * frame.dir.x + cos * frame.norm.x, y: -sin * frame.dir.y + cos * frame.norm.y },
        width: frame.width,
        depth: frame.depth,
        z: frame.z,
      });
    emitAll({
      map: (p) => {
        const du = mapU.map(p[0]) - c[0];
        const dn = mapN.map(p[1]) - c[1];
        return [c[0] + du * cos - dn * sin, c[1] + du * sin + dn * cos];
      },
      scaleU: Math.abs(mapU.scale),
      scaleN: Math.abs(mapN.scale),
    });
    if (instanced) endGlyphInstance(sink.out);
  }
}

/** Shapes outside every group behave like one stretch/stretch group. */
function withImplicitGroup(spec: ObjectDefinitionSpec): ObjectGroup[] {
  const grouped = new Set(spec.groups.flatMap((g) => g.shapeIds));
  const loose = spec.shapes.filter((s) => !grouped.has(s.id)).map((s) => s.id);
  const implicit: ObjectGroup = { id: '_loose', name: '', shapeIds: loose, resize: { u: 'stretch', n: 'stretch' } };
  return loose.length > 0 ? [implicit, ...spec.groups] : [...spec.groups];
}

/**
 * Glyph segments of a custom object in `frame` (`[x1,y1,z, x2,y2,z, …]`).
 * `layer` selects the always-drawn glyph or the selection-only extras. With
 * `live.dashedOut`, dashed lines are left out of the result and collected
 * there as continuous lines instead (the editor dashes them in screen pixels,
 * like the shapes being drawn).
 */
export function evaluateCustomGlyph(
  spec: ObjectDefinitionSpec,
  frame: SketchObjectFrame,
  rawParams: CustomParamValues,
  layer: ObjectShapeLayer = 'glyph',
  live: { height?: number; wall?: number; offset?: number; dashedOut?: number[] } = {},
): number[] {
  const out: number[] = [];
  const sink = new SegmentSink(out, frame, live.dashedOut ?? null);
  const params = resolveCustomParams(spec, rawParams);
  const vars = evaluationVars(spec, frame.width, frame.depth, params, live.height, live.wall, live.offset);
  for (const group of withImplicitGroup(spec)) {
    if (sink.full) break;
    emitGroup(sink, spec, group, frame, params, layer, vars);
  }
  return out;
}
